import MarkdownIt from "markdown-it";
import * as Y from "yjs";
import { applyLineEdits } from "./markdown-patch.js";
import { topLevelBlockToMarkdown } from "./yjs-markdown.js";
const YJS_FRAGMENT = "prosemirror";
const MAX_HEADING_LEVEL = 3;
const TASK_PREFIX = /^\[([ xX])\]\s+/;
const markdown = new MarkdownIt({ html: false, linkify: true });
function paragraph(content = []) {
    return content.length > 0
        ? { type: "paragraph", content }
        : { type: "paragraph" };
}
// ── Inline ───────────────────────────────────────────────────────────────
function sameMarks(a = [], b = []) {
    return (a.length === b.length &&
        a.every((mark, index) => mark.type === b[index]?.type &&
            JSON.stringify(mark.attrs ?? {}) ===
                JSON.stringify(b[index]?.attrs ?? {})));
}
function pushText(nodes, text, marks) {
    if (!text)
        return;
    const previous = nodes[nodes.length - 1];
    if (previous?.type === "text" && sameMarks(previous.marks, marks)) {
        previous.text += text;
        return;
    }
    nodes.push(marks.length > 0
        ? { type: "text", text, marks: marks.map((mark) => ({ ...mark })) }
        : { type: "text", text });
}
/** Inline tokens → text/hardBreak/image nodes. Images are returned inline
 *  here and lifted to block level by `liftImages`. */
function parseInline(tokens = []) {
    const nodes = [];
    const marks = [];
    const removeMark = (type) => {
        const index = marks.map((mark) => mark.type).lastIndexOf(type);
        if (index >= 0)
            marks.splice(index, 1);
    };
    for (const token of tokens) {
        switch (token.type) {
            case "text":
            case "html_inline":
                pushText(nodes, token.content, marks);
                break;
            case "softbreak":
                pushText(nodes, " ", marks);
                break;
            case "hardbreak":
                nodes.push({ type: "hardBreak" });
                break;
            case "code_inline":
                // Tiptap's code mark excludes every other mark.
                pushText(nodes, token.content, [{ type: "code" }]);
                break;
            case "strong_open":
                marks.push({ type: "bold" });
                break;
            case "strong_close":
                removeMark("bold");
                break;
            case "em_open":
                marks.push({ type: "italic" });
                break;
            case "em_close":
                removeMark("italic");
                break;
            case "s_open":
                marks.push({ type: "strike" });
                break;
            case "s_close":
                removeMark("strike");
                break;
            case "link_open":
                marks.push({ type: "link", attrs: { href: token.attrGet("href") } });
                break;
            case "link_close":
                removeMark("link");
                break;
            case "image": {
                const attrs = {
                    src: token.attrGet("src"),
                    alt: token.content || null,
                };
                const title = token.attrGet("title");
                if (title)
                    attrs.title = title;
                nodes.push({ type: "image", attrs });
                break;
            }
            default:
                if (token.content)
                    pushText(nodes, token.content, marks);
        }
    }
    return nodes;
}
/** The editor's image node is a block, so split a paragraph around images. */
function liftImages(inline) {
    if (!inline.some((node) => node.type === "image"))
        return [paragraph(inline)];
    const blocks = [];
    let run = [];
    const flush = () => {
        while (run[0]?.type === "hardBreak")
            run.shift();
        while (run[run.length - 1]?.type === "hardBreak")
            run.pop();
        const text = run.map((node) => node.text ?? "").join("");
        if (run.some((node) => node.type !== "text") || text.trim()) {
            blocks.push(paragraph(run));
        }
        run = [];
    };
    for (const node of inline) {
        if (node.type === "image") {
            flush();
            blocks.push(node);
        }
        else {
            run.push(node);
        }
    }
    flush();
    return blocks;
}
/** List and task items must start with a paragraph. */
function ensureLeadingParagraph(content) {
    return content[0]?.type === "paragraph" ? content : [paragraph(), ...content];
}
function parseBlocks(cursor, closeType) {
    const blocks = [];
    while (cursor.index < cursor.tokens.length) {
        const token = cursor.tokens[cursor.index];
        if (!token)
            break;
        if (closeType && token.type === closeType) {
            cursor.index++;
            return blocks;
        }
        cursor.index++;
        switch (token.type) {
            case "paragraph_open": {
                const inline = cursor.tokens[cursor.index];
                cursor.index += 2; // inline + paragraph_close
                blocks.push(...liftImages(parseInline(inline?.children ?? [])));
                break;
            }
            case "heading_open": {
                const inline = cursor.tokens[cursor.index];
                cursor.index += 2; // inline + heading_close
                const level = Math.min(MAX_HEADING_LEVEL, Number(token.tag.slice(1)) || 1);
                const [first, ...rest] = liftImages(parseInline(inline?.children ?? []));
                blocks.push({
                    type: "heading",
                    attrs: { level },
                    ...(first?.type === "paragraph" && first.content
                        ? { content: first.content }
                        : {}),
                });
                if (first && first.type !== "paragraph")
                    blocks.push(first);
                blocks.push(...rest);
                break;
            }
            case "bullet_list_open":
                blocks.push(toTaskList(parseList(cursor, "bullet_list_close")));
                break;
            case "ordered_list_open": {
                const start = Number(token.attrGet("start") ?? 1);
                const list = {
                    type: "orderedList",
                    content: parseList(cursor, "ordered_list_close"),
                };
                if (start !== 1)
                    list.attrs = { start };
                blocks.push(list);
                break;
            }
            case "blockquote_open": {
                const content = parseBlocks(cursor, "blockquote_close");
                blocks.push({
                    type: "blockquote",
                    content: content.length > 0 ? content : [paragraph()],
                });
                break;
            }
            case "fence":
            case "code_block": {
                const text = token.content.replace(/\n$/, "");
                const language = token.info.trim().split(/\s+/)[0] || null;
                blocks.push({
                    type: "codeBlock",
                    ...(language ? { attrs: { language } } : {}),
                    ...(text ? { content: [{ type: "text", text }] } : {}),
                });
                break;
            }
            case "hr":
                blocks.push({ type: "horizontalRule" });
                break;
            case "table_open":
                blocks.push(parseTable(cursor));
                break;
            case "html_block": {
                const text = token.content.trim();
                if (text)
                    blocks.push(paragraph([{ type: "text", text }]));
                break;
            }
            default:
                // Unknown block tokens carry no content of their own.
                break;
        }
    }
    return blocks;
}
function parseList(cursor, closeType) {
    const items = [];
    while (cursor.index < cursor.tokens.length) {
        const token = cursor.tokens[cursor.index++];
        if (!token || token.type === closeType)
            break;
        if (token.type === "list_item_open") {
            items.push({
                type: "listItem",
                content: ensureLeadingParagraph(parseBlocks(cursor, "list_item_close")),
            });
        }
    }
    return items;
}
/** A bullet list whose items all start with `[ ]` or `[x]` is a task list. */
function toTaskList(items) {
    const firstText = (item) => item.content?.[0]?.content?.[0]?.type === "text"
        ? item.content[0].content[0]
        : undefined;
    const isTaskList = items.length > 0 &&
        items.every((item) => TASK_PREFIX.test(firstText(item)?.text ?? ""));
    if (!isTaskList)
        return { type: "bulletList", content: items };
    return {
        type: "taskList",
        content: items.map((item) => {
            const text = firstText(item);
            const match = TASK_PREFIX.exec(text.text ?? "");
            text.text = (text.text ?? "").slice(match[0].length);
            const lead = item.content[0];
            if (!text.text)
                lead.content.shift();
            if (lead.content?.length === 0)
                delete lead.content;
            return {
                type: "taskItem",
                attrs: { checked: match[1] !== " " },
                content: item.content,
            };
        }),
    };
}
function parseTable(cursor) {
    const rows = [];
    let row = null;
    while (cursor.index < cursor.tokens.length) {
        const token = cursor.tokens[cursor.index++];
        if (!token || token.type === "table_close")
            break;
        if (token.type === "tr_open")
            row = [];
        else if (token.type === "tr_close" && row) {
            rows.push({ type: "tableRow", content: row });
            row = null;
        }
        else if ((token.type === "th_open" || token.type === "td_open") && row) {
            const inline = cursor.tokens[cursor.index];
            cursor.index += 2; // inline + th_close/td_close
            const content = liftImages(parseInline(inline?.children ?? []));
            row.push({
                type: token.type === "th_open" ? "tableHeader" : "tableCell",
                content: content.length > 0 ? content : [paragraph()],
            });
        }
    }
    return { type: "table", content: rows };
}
function parseMarkdownBlocks(source) {
    return parseBlocks({ index: 0, tokens: markdown.parse(source, {}) });
}
/** Parses markdown into ProseMirror JSON matching the editor's schema. */
export function markdownToProseMirrorJSON(source) {
    const content = parseMarkdownBlocks(source);
    return { type: "doc", content: content.length > 0 ? content : [paragraph()] };
}
// ── Yjs ──────────────────────────────────────────────────────────────────
function appendNodes(parent, nodes = []) {
    insertNodes(parent, parent.length, nodes);
}
/** Builds `nodes` as Yjs types and inserts them into `parent` at `at`. */
function insertNodes(parent, at, nodes = []) {
    let index = 0;
    while (index < nodes.length) {
        const node = nodes[index];
        if (node.type === "text") {
            // y-prosemirror keeps a run of adjacent text nodes in one XmlText, with
            // each mark stored as a formatting attribute holding the mark's attrs.
            const delta = [];
            while (nodes[index]?.type === "text") {
                const text = nodes[index];
                const attributes = {};
                for (const mark of text.marks ?? []) {
                    attributes[mark.type] = mark.attrs ?? {};
                }
                delta.push({ insert: text.text ?? "", attributes });
                index++;
            }
            const yText = new Y.XmlText();
            parent.insert(at++, [yText]);
            yText.applyDelta(delta);
            continue;
        }
        const element = new Y.XmlElement(node.type);
        parent.insert(at++, [element]);
        for (const [key, value] of Object.entries(node.attrs ?? {})) {
            if (value !== null && value !== undefined) {
                element.setAttribute(key, value);
            }
        }
        appendNodes(element, node.content);
        index++;
    }
}
/** Above this many cells the block LCS gives up and replaces the range. */
const MAX_LCS_CELLS = 4_000_000;
/** The Markdown of each node as `yDocToMarkdown` would write it. */
function serializeNodes(nodes) {
    const scratch = new Y.Doc();
    try {
        const fragment = scratch.getXmlFragment(YJS_FRAGMENT);
        appendNodes(fragment, nodes);
        return fragment.toArray().map(topLevelBlockToMarkdown);
    }
    finally {
        scratch.destroy();
    }
}
/** Index pairs of a longest common subsequence of `a` and `b`. */
function commonBlocks(a, b) {
    let prefix = 0;
    while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) {
        prefix++;
    }
    let suffix = 0;
    while (suffix < a.length - prefix &&
        suffix < b.length - prefix &&
        a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) {
        suffix++;
    }
    const pairs = [];
    for (let index = 0; index < prefix; index++)
        pairs.push([index, index]);
    const n = a.length - prefix - suffix;
    const m = b.length - prefix - suffix;
    if (n > 0 && m > 0 && (n + 1) * (m + 1) <= MAX_LCS_CELLS) {
        // lengths[i][j] = LCS of a[prefix+i…] and b[prefix+j…]
        const width = m + 1;
        const lengths = new Uint32Array((n + 1) * width);
        for (let i = n - 1; i >= 0; i--) {
            for (let j = m - 1; j >= 0; j--) {
                lengths[i * width + j] =
                    a[prefix + i] === b[prefix + j]
                        ? lengths[(i + 1) * width + j + 1] + 1
                        : Math.max(lengths[(i + 1) * width + j], lengths[i * width + j + 1]);
            }
        }
        let i = 0;
        let j = 0;
        while (i < n && j < m) {
            if (a[prefix + i] === b[prefix + j]) {
                pairs.push([prefix + i, prefix + j]);
                i++;
                j++;
            }
            else if (lengths[(i + 1) * width + j] >= lengths[i * width + j + 1]) {
                i++;
            }
            else {
                j++;
            }
        }
    }
    for (let index = suffix; index > 0; index--) {
        pairs.push([a.length - index, b.length - index]);
    }
    return pairs;
}
/**
 * Replaces the fragment children `[from, to)` with `nodes`, keeping every
 * child whose Markdown is unchanged (same Yjs items, attributes and
 * cursors). Must run inside a transaction.
 */
function reconcileBlocks(fragment, from, to, nodes) {
    const before = fragment
        .slice(from, to)
        .map((child) => topLevelBlockToMarkdown(child));
    const after = serializeNodes(nodes);
    const pairs = commonBlocks(before, after);
    // Walk the gaps between kept blocks from the end, so indices before the
    // current gap stay valid.
    let removed = 0;
    let inserted = 0;
    let oldEnd = before.length;
    let newEnd = after.length;
    for (let index = pairs.length; index >= 0; index--) {
        const [oldKept, newKept] = pairs[index - 1] ?? [-1, -1];
        const oldStart = oldKept + 1;
        const newStart = newKept + 1;
        if (oldEnd > oldStart) {
            fragment.delete(from + oldStart, oldEnd - oldStart);
            removed += oldEnd - oldStart;
        }
        if (newEnd > newStart) {
            insertNodes(fragment, from + oldStart, nodes.slice(newStart, newEnd));
            inserted += newEnd - newStart;
        }
        oldEnd = oldKept;
        newEnd = newKept;
    }
    return { kept: before.length - removed, removed, inserted };
}
/** The editor needs at least one block. */
function ensureBlock(fragment, changes) {
    if (fragment.length > 0)
        return changes;
    insertNodes(fragment, 0, [paragraph()]);
    return { ...changes, inserted: changes.inserted + 1 };
}
/**
 * Replaces the editor content of `doc` with `source` in one transaction.
 * Top-level blocks whose Markdown is unchanged are kept as they are, so
 * concurrent edits, cursors and non-Markdown attributes in them survive.
 * Use this on a document connected to the collaboration server so the change
 * syncs to every open editor.
 */
export function replaceYDocWithMarkdown(doc, source, origin) {
    const fragment = doc.getXmlFragment(YJS_FRAGMENT);
    const nodes = parseMarkdownBlocks(source);
    let changes = { kept: 0, removed: 0, inserted: 0 };
    doc.transact(() => {
        changes = ensureBlock(fragment, reconcileBlocks(fragment, 0, fragment.length, nodes));
    }, origin);
    return changes;
}
/**
 * Applies line edits (from `applyHunks`) to the Markdown of `doc`'s editor
 * content. Only the top-level blocks the edits touch are re-parsed and
 * reconciled; the edits' line numbers refer to `yDocToMarkdown(doc)`.
 */
export function applyLineEditsToYDoc(doc, edits, origin) {
    const fragment = doc.getXmlFragment(YJS_FRAGMENT);
    const blocks = fragment.toArray().map(topLevelBlockToMarkdown);
    // The lines of yDocToMarkdown(doc), each with the block it belongs to
    // (-1 for the blank line between two blocks).
    const lines = [];
    const owner = [];
    const first = [];
    const last = [];
    blocks.forEach((block, index) => {
        if (index > 0) {
            lines.push("");
            owner.push(-1);
        }
        first.push(lines.length);
        for (const line of block.split("\n")) {
            lines.push(line);
            owner.push(index);
        }
        last.push(lines.length - 1);
    });
    // An empty document has no lines; the edits rebuild it.
    if (blocks.join("\n\n") === "") {
        const source = edits.flatMap((edit) => edit.lines).join("\n");
        return replaceYDocWithMarkdown(doc, source, origin);
    }
    const regions = [];
    for (const edit of edits) {
        let from = edit.start;
        let to = edit.end;
        for (let line = Math.max(0, edit.start - 1); line <= Math.min(lines.length - 1, edit.end); line++) {
            const block = owner[line];
            if (block < 0)
                continue;
            from = Math.min(from, first[block]);
            to = Math.max(to, last[block] + 1);
        }
        const previous = regions[regions.length - 1];
        if (previous && from <= previous.to) {
            previous.from = Math.min(previous.from, from);
            previous.to = Math.max(previous.to, to);
            previous.edits.push(edit);
        }
        else {
            regions.push({ from, to, edits: [edit] });
        }
    }
    const changes = {
        kept: blocks.length,
        removed: 0,
        inserted: 0,
    };
    doc.transact(() => {
        for (const region of regions.reverse()) {
            // Regions never cut through a block, so the blocks inside are exactly
            // those that start in it.
            let blockFrom = 0;
            while (blockFrom < blocks.length && first[blockFrom] < region.from) {
                blockFrom++;
            }
            let blockTo = blockFrom;
            while (blockTo < blocks.length && first[blockTo] < region.to)
                blockTo++;
            const source = applyLineEdits(lines, region.edits, region.from, region.to);
            const result = reconcileBlocks(fragment, blockFrom, blockTo, parseMarkdownBlocks(source));
            changes.kept -= result.removed;
            changes.removed += result.removed;
            changes.inserted += result.inserted;
        }
        Object.assign(changes, ensureBlock(fragment, changes));
    }, origin);
    return changes;
}
/**
 * Appends `source` to the end of the editor content of `doc` in one
 * transaction, leaving existing content untouched.
 */
export function appendMarkdownToYDoc(doc, source, origin) {
    const fragment = doc.getXmlFragment(YJS_FRAGMENT);
    const json = markdownToProseMirrorJSON(source);
    doc.transact(() => appendNodes(fragment, json.content), origin);
}
/** Builds a fresh Y.Doc holding `source` as editor content. */
export function markdownToYDoc(source) {
    const doc = new Y.Doc();
    replaceYDocWithMarkdown(doc, source);
    return doc;
}
/**
 * Encodes `source` as a Yjs update, e.g. for the initial `documents.data` of
 * a document no editor has opened yet.
 */
export function markdownToYjsUpdate(source) {
    const doc = markdownToYDoc(source);
    try {
        return Y.encodeStateAsUpdate(doc);
    }
    finally {
        doc.destroy();
    }
}
